/*----------------------------------
- PHONE DRAWER: THE GRAB BAR'S DRAG UP
----------------------------------*/

/**
 * The drag up that closes the phone drawer from its grab bar (`.msm-grab`), as the page template's script does
 * (docs/mockups/website-page-template.html, shell:script): the drawer follows the finger once it has moved 8px up, the
 * scrim fades with it, and on release the drawer closes past a quarter of its height or on a flick faster than
 * 0.6px a millisecond, else it springs back. The click that ends a drag is swallowed, so it is not read as a tap on the
 * grab bar (a tap closes too, through the bar's own click). Written by hand: no gesture library for one handle.
 *
 * @docs docs/features/website-navigation-shell
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import React from 'react';

/*----------------------------------
- TYPES
----------------------------------*/

type GrabDrag = { pointerId: number; startY: number; startTime: number; distance: number; following: boolean };

/** A ref the drawer and the scrim hand in (`useRef`), null before they mount. */
type ElementRef = { readonly current: HTMLElement | null };

/*----------------------------------
- CONSTANTS
----------------------------------*/

/** How far up the finger moves before the drawer follows it, so a tap with a shaky finger stays a tap. */
const DRAG_START_PX = 8;
/** Past this share of the drawer's height, a release closes it. */
const CLOSE_SHARE = 0.25;
/** A flick faster than this (px per ms) closes it whatever the distance. */
const CLOSE_VELOCITY = 0.6;
/** How long the click that follows a drag is swallowed, in ms. */
const SWALLOW_CLICK_MS = 350;
/** The classes that drop the transitions while the finger holds the drawer (marketing.css). */
const DRAGGING_CLASS = 'is-drag';

/*----------------------------------
- HELPERS
----------------------------------*/

/** Whether a drag released `distance` px up after `elapsedMs` closes a drawer `drawerHeight` px tall. */
export const shouldCloseDrawer = (distance: number, drawerHeight: number, elapsedMs: number): boolean =>
    distance > drawerHeight * CLOSE_SHARE || distance / Math.max(1, elapsedMs) > CLOSE_VELOCITY;

/*----------------------------------
- HOOK
----------------------------------*/

/**
 * Returns the grab bar's `pointerdown` handler and a capture-phase click handler for the drawer, which swallows the
 * click that ends a drag. `drawerRef` and `scrimRef` are the elements the drag moves; `onClose` closes the drawer.
 */
export default function useDrawerGrabClose(
    drawerRef: ElementRef,
    scrimRef: ElementRef,
    onClose: () => void,
) {
    const dragRef = React.useRef<GrabDrag | null>(null);
    const swallowRef = React.useRef(false);
    const closeRef = React.useRef(onClose);
    closeRef.current = onClose;
    // The window listeners live only for one drag (from the grab bar's pointerdown to its release), not for the page's
    // life: the hook is mounted on every page, closed drawer included (self-review m4, 2026-11-04).
    const listenRef = React.useRef<{ attach: () => void; detach: () => void } | null>(null);

    React.useEffect(() => {
        const settle = (drawer: HTMLElement, scrim: HTMLElement | null) => {
            drawer.classList.remove(DRAGGING_CLASS);
            scrim?.classList.remove(DRAGGING_CLASS);
            drawer.style.transform = '';
            if (scrim) scrim.style.opacity = '';
        };

        const handleMove = (event: PointerEvent) => {
            const drag = dragRef.current;
            const drawer = drawerRef.current;
            if (!drag || !drawer || event.pointerId !== drag.pointerId) return;

            const travelled = drag.startY - event.clientY;
            if (!drag.following) {
                if (travelled < DRAG_START_PX) return;
                drag.following = true;
                drawer.classList.add(DRAGGING_CLASS);
                scrimRef.current?.classList.add(DRAGGING_CLASS);
            }

            drag.distance = Math.max(0, travelled);
            drawer.style.transform = `translateY(${-drag.distance}px)`;
            if (scrimRef.current) scrimRef.current.style.opacity = String(1 - Math.min(1, drag.distance / drawer.offsetHeight));
        };

        const handleRelease = (event: PointerEvent) => {
            const drag = dragRef.current;
            const drawer = drawerRef.current;
            if (!drag || event.pointerId !== drag.pointerId) return;

            dragRef.current = null;
            detach();
            if (!drag.following || !drawer) return;

            swallowRef.current = true;
            window.setTimeout(() => {
                swallowRef.current = false;
            }, SWALLOW_CLICK_MS);
            settle(drawer, scrimRef.current);

            // A cancelled gesture (the browser took the pointer for a scroll or a system gesture) springs back: only a
            // real release may close through the distance or flick rule.
            if (event.type === 'pointercancel') return;
            if (shouldCloseDrawer(drag.distance, drawer.offsetHeight, event.timeStamp - drag.startTime)) closeRef.current();
        };

        const attach = () => {
            window.addEventListener('pointermove', handleMove);
            window.addEventListener('pointerup', handleRelease);
            window.addEventListener('pointercancel', handleRelease);
        };
        const detach = () => {
            window.removeEventListener('pointermove', handleMove);
            window.removeEventListener('pointerup', handleRelease);
            window.removeEventListener('pointercancel', handleRelease);
        };
        listenRef.current = { attach, detach };
        return () => {
            detach();
            listenRef.current = null;
        };
    }, [drawerRef, scrimRef]);

    const onGrabPointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
        if (event.button) return;
        dragRef.current = { pointerId: event.pointerId, startY: event.clientY, startTime: event.timeStamp, distance: 0, following: false };
        listenRef.current?.detach();
        listenRef.current?.attach();
    };

    const onDrawerClickCapture = (event: React.MouseEvent<HTMLElement>) => {
        if (!swallowRef.current) return;
        event.preventDefault();
        event.stopPropagation();
    };

    return { onGrabPointerDown, onDrawerClickCapture };
}
